import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Flame, MapPin, Lock, TrendingUp } from "lucide-react";
import { api, fmtMoney } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BentoCard, BrutalButton } from "@/components/dashboard/BentoCard";
import { PropertyDrawer } from "@/components/dashboard/PropertyDrawer";
import { Link } from "react-router-dom";

const SCORE_FILTERS = [
  { label: "All", value: 0 },
  { label: "60+", value: 60 },
  { label: "75+", value: 75 },
  { label: "90+", value: 90 },
];

function scoreColor(score) {
  if (score >= 85) return "bg-red-600 text-white";
  if (score >= 70) return "bg-orange-500 text-white";
  if (score >= 55) return "bg-yellow-400 text-black";
  return "bg-zinc-200 text-zinc-700";
}

function ScoreHex({ score }) {
  return (
    <div
      className={`flex flex-col items-center justify-center w-16 h-16 shrink-0 border-2 border-black ${scoreColor(score)}`}
      style={{ clipPath: "polygon(25% 5%, 75% 5%, 100% 50%, 75% 95%, 25% 95%, 0% 50%)" }}
    >
      <span className="font-display font-bold text-xl leading-none">{score}</span>
      <span className="font-mono-data text-[8px] uppercase tracking-widest">score</span>
    </div>
  );
}

export default function HotDealsPage() {
  const { isSubscribed } = useAuth();
  const [minScore, setMinScore] = useState(0);
  const [drawer, setDrawer] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["hot-deals", minScore],
    queryFn: async () => (await api.get(`/hot-deals?limit=50&min_score=${minScore}`)).data,
    staleTime: 60000,
  });

  const items = data?.items || [];
  const locked = data?.locked;

  return (
    <div data-testid="hot-deals-root" className="space-y-6">
      <div>
        <div className="data-label">Across every county</div>
        <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
          <Flame size={30} className="text-red-600" /> Hot Deals
        </h1>
        <p className="text-sm text-zinc-600 mt-2">
          The highest AI Deal Scores from all your live sources — no filtering by county needed.
          {data?.scanned ? (
            <span className="font-mono-data text-zinc-400"> ({data.scanned.toLocaleString()} parcels ranked)</span>
          ) : null}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="hot-deals-filters">
        <span className="data-label mr-1">Min score</span>
        {SCORE_FILTERS.map((f) => (
          <button
            key={f.value}
            data-testid={`hot-deals-filter-${f.value}`}
            onClick={() => setMinScore(f.value)}
            className={`border-2 border-black px-3 py-1 font-display font-semibold text-sm shadow-brutal-sm transition-colors ${
              minScore === f.value ? "bg-red-600 text-white" : "bg-white hover:bg-yellow-100"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {locked ? (
        <div
          data-testid="hot-deals-paywall"
          className="border-2 border-black bg-yellow-50 p-4 flex items-center gap-3 shadow-brutal-sm"
        >
          <Lock size={18} className="text-red-600 shrink-0" />
          <p className="text-sm text-zinc-700">
            Owner names, exact amounts and addresses are masked.{" "}
            <Link to="/pricing" className="text-red-600 font-semibold hover:underline">
              Subscribe
            </Link>{" "}
            to unlock full deal details.
          </p>
        </div>
      ) : null}

      {isLoading && <p className="text-zinc-500">Ranking deals…</p>}

      {!isLoading && items.length === 0 && (
        <BentoCard className="text-center py-12" data-testid="hot-deals-empty">
          <TrendingUp size={48} className="mx-auto text-yellow-400" strokeWidth={1.5} />
          <p className="mt-3 font-display text-lg font-semibold">No deals at this score yet</p>
          <p className="text-sm text-zinc-500">Try lowering the minimum score.</p>
        </BentoCard>
      )}

      <div className="grid gap-3">
        {items.map((it, i) => {
          const score = it.deal_score?.score ?? 0;
          return (
            <button
              key={it.id || i}
              data-testid={`hot-deal-card-${i}`}
              onClick={() => setDrawer(it)}
              className="text-left bg-white border-2 border-black p-4 shadow-brutal-sm hover:-translate-y-0.5 hover:shadow-brutal transition-transform flex items-center gap-4"
            >
              <div className="font-display font-bold text-2xl text-zinc-300 w-8 shrink-0 text-center">
                {i + 1}
              </div>
              <ScoreHex score={score} />
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-base truncate">
                  {it.address || it.parcel_number || "—"}
                </div>
                <div className="text-sm text-zinc-500 flex items-center gap-1 mt-0.5">
                  <MapPin size={12} />
                  {[it.city, it.state].filter(Boolean).join(", ")} · {it.jurisdiction}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-mono-data text-[10px] uppercase tracking-widest text-red-600">
                    {it.deal_score?.label}
                  </span>
                  {it.status ? (
                    <span className="font-mono-data text-[10px] uppercase text-zinc-400">{it.status}</span>
                  ) : null}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="data-label">Owed</div>
                <div className="font-mono-data font-bold text-lg">{fmtMoney(it.amount_owed)}</div>
              </div>
            </button>
          );
        })}
      </div>

      {locked && items.length > 0 ? (
        <div className="pt-2">
          <Link to="/pricing">
            <BrutalButton data-testid="hot-deals-subscribe-btn">Unlock full details</BrutalButton>
          </Link>
        </div>
      ) : null}

      {drawer ? <PropertyDrawer row={drawer} onClose={() => setDrawer(null)} /> : null}
    </div>
  );
}
